import { useCallback, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../../hooks/useAppStore';
import { baseApi } from '../../../store/api/baseApi';

export interface ChatAttachment {
  type:     'image';
  url:      string;
  mimeType?: string;
  name?:     string;
  size?:     number;
}

export type StreamMessage =
  | { id: string; role: 'user';      content: string; createdAt: Date; attachments?: ChatAttachment[] }
  | { id: string; role: 'assistant'; content: string; toolsUsed: string[]; createdAt: Date; attachments?: ChatAttachment[] }
  // reason 'wallet' = se acabaron los créditos del agente (comprar más);
  // reason 'quota'  = se agotó el cupo mensual del plan de la org (subir de plan).
  | { id: string; role: 'system';    kind: 'no_credits'; reason: 'wallet' | 'quota'; balance?: number; required?: number; limit?: number;
      /** Cuota mensual por agente (Imperium: 300). null/undefined = el tenant va por pool del plan. */
      quota?: number | null;
      /** El cupo se renueva el 1° de cada mes (solo régimen por-agente). */
      renewsMonthly?: boolean;
      createdAt: Date };

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000/api/v2';

/**
 * Hook que maneja el chat en streaming con Gustavo.
 * Usa fetch + ReadableStream para parsear SSE (no EventSource porque no podemos mandar Authorization header).
 */
interface InitialMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Del agente: lo que adjuntó. De Gustavo: lo que le mandó por WhatsApp (piezas, fotos). */
  attachments?: ChatAttachment[];
  /** Por dónde entró: el hilo es uno solo y la burbuja marca lo que vino del celular. */
  channel?: 'crm' | 'whatsapp';
  createdAt: string;
}

export interface CreditsEvent {
  event: 'charged' | 'refunded';
  balance: number;
  cost?: number;
  ownerType?: 'user' | 'office';
}

export interface InsufficientCreditsError {
  kind: 'insufficient_credits';
  /** Ya viene en 0 cuando el saldo real quedó negativo: al agente no le sirve un -3,5. */
  balance: number;
  /** Cuota mensual por agente, o null si el tenant va por el pool de su plan. */
  quota?: number | null;
  renewsMonthly?: boolean;
  message: string;
}

interface UseGustavoChatOptions {
  onNavigate?: (to: string, reason?: string) => void;
  onCredits?: (event: CreditsEvent) => void;
  onInsufficientCredits?: (err: InsufficientCreditsError) => void;
  /** Se llama cuando Gustavo ejecuta una tool (por nombre) → el consumidor invalida cache. */
  onToolUsed?: (name: string) => void;
}

export function useGustavoChat(
  initialConversationId?: string,
  initialMessages?: InitialMessage[],
  options?: UseGustavoChatOptions,
) {
  const onNavigate = options?.onNavigate;
  const onCredits = options?.onCredits;
  const onInsufficientCredits = options?.onInsufficientCredits;
  const onToolUsed = options?.onToolUsed;
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const [messages, setMessages] = useState<StreamMessage[]>(() =>
    (initialMessages ?? []).map((m, i) => ({
      id: `init-${i}`,
      role: m.role,
      content: m.content,
      channel: m.channel,
      createdAt: new Date(m.createdAt),
      toolsUsed: [],
      ...(m.attachments?.length ? { attachments: m.attachments } : {}),
    } as StreamMessage)),
  );
  const [isStreaming, setIsStreaming] = useState(false);
  const dispatch = useAppDispatch();
  const [conversationId, setConversationId] = useState<string | undefined>(initialConversationId);
  const [error, setError] = useState<string | null>(null);
  const [undoable, setUndoable] = useState<{ logId: string; expiresAt: number } | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const resetTo = useCallback((conv?: { _id: string; messages: { role: 'user' | 'assistant'; content: string; createdAt: string; attachments?: ChatAttachment[] }[] }) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
    setError(null);
    setIsStreaming(false);
    if (!conv) {
      setMessages([]);
      setConversationId(undefined);
      return;
    }
    setConversationId(conv._id);
    setMessages(conv.messages.map((m, i) => ({
      id: `${conv._id}-${i}`,
      role: m.role,
      content: m.content,
      createdAt: new Date(m.createdAt),
      toolsUsed: [],
      ...(m.attachments?.length ? { attachments: m.attachments } : {}),
    } as StreamMessage)));
  }, []);

  /**
   * Mete una página VIEJA del historial arriba de todo (el scroll hacia arriba del chat).
   *
   * Vive acá y no en el hook del historial porque el estado que se ve mezcla dos fuentes: lo
   * que vino del backend y lo que se está mandando en vivo. Se filtra por id para que una
   * página pedida dos veces —el scroll dispara de a ráfagas— no duplique burbujas.
   */
  const prepend = useCallback((viejos: InitialMessage[]) => {
    if (viejos.length === 0) return;
    setMessages((prev) => {
      const yaEstan = new Set(prev.map((m) => m.id));
      const nuevos = viejos
        .map((m, i) => ({
          // El id lleva la fecha del mensaje y no solo el índice: los índices de una página
          // vieja chocan con los de la primera (`init-0`, `init-1`…) y React reusaba la
          // burbuja equivocada.
          id: `hist-${new Date(m.createdAt).getTime()}-${i}-${m.role}`,
          role: m.role,
          content: m.content,
          channel: (m as { channel?: 'crm' | 'whatsapp' }).channel,
          createdAt: new Date(m.createdAt),
          toolsUsed: [],
          ...(m.attachments?.length ? { attachments: m.attachments } : {}),
        } as StreamMessage))
        .filter((m) => !yaEstan.has(m.id));
      return nuevos.length ? [...nuevos, ...prev] : prev;
    });
  }, []);

  const send = useCallback(async (
    text: string,
    opts?: { attachments?: ChatAttachment[] },
  ) => {
    const trimmed = text.trim();
    const attachments = opts?.attachments ?? [];
    if ((!trimmed && attachments.length === 0) || isStreaming) return;

    const userMsg: StreamMessage = {
      id:      `user-${Date.now()}`,
      role:    'user',
      content: trimmed,
      createdAt: new Date(),
      attachments: attachments.length > 0 ? attachments : undefined,
    };
    const assistantId = `assistant-${Date.now()}`;
    const assistantMsg: StreamMessage = {
      id:        assistantId,
      role:      'assistant',
      content:   '',
      toolsUsed: [],
      createdAt: new Date(),
    };

    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setIsStreaming(true);
    setError(null);

    const ac = new AbortController();
    abortRef.current = ac;

    // Acumulamos los deltas en memoria y solo flusheamos al recibir `done`,
    // así el mensaje aparece de una sola vez (mientras tanto el bubble vacío
    // muestra los 3 puntitos de typing).
    let accumulatedContent = '';
    let errored = false;

    try {
      const res = await fetch(`${BASE_URL}/ai/chat`, {
        method:  'POST',
        signal:  ac.signal,
        credentials: 'include',
        headers: {
          'Content-Type':  'application/json',
          'Accept':        'text/event-stream',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          message:        trimmed || '(adjuntos)',
          conversationId,
          ...(attachments.length > 0 ? { attachments } : {}),
        }),
      });

      if (!res.ok || !res.body) {
        const errMsg = res.status === 503
          ? 'El asistente está deshabilitado en este momento.'
          : res.status === 403 ? 'Tu rol no tiene acceso al asistente.'
          : `Error ${res.status}`;
        throw new Error(errMsg);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      // Tools del turno: se vuelven a invalidar al TERMINAR. Al empezar la tool todavía no
      // escribió y la tarjeta de la propiedad se volvía a pedir con el precio viejo
      // ("Propiedad actualizada" con UYU 25.000 en vez de 26.500, batería 4, 25/09/2026).
      const toolsDelTurno = new Set<string>();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parsear eventos SSE completos (delimitados por \n\n)
        let idx: number;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const rawEvent = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          parseEvent(rawEvent, {
            onDelta: (text) => {
              accumulatedContent += text;
            },
            onToolStart: (name) => {
              setMessages((prev) => prev.map((m) =>
                m.id === assistantId && m.role === 'assistant'
                  ? { ...m, toolsUsed: [...m.toolsUsed, name] }
                  : m,
              ));
              toolsDelTurno.add(name);
              onToolUsed?.(name);
            },
            onToolEnd: (name) => {
              toolsDelTurno.add(name);
              onToolUsed?.(name);
            },
            onCreated: (data) => {
              if (data?.conversationId) setConversationId(data.conversationId);
            },
            onNavigate: (data) => {
              if (data?.to && typeof data.to === 'string') {
                onNavigate?.(data.to, data.reason);
              }
            },
            onDone: (data) => {
              setMessages((prev) => prev.map((m) =>
                m.id === assistantId && m.role === 'assistant'
                  ? { ...m, content: accumulatedContent }
                  : m,
              ));
              if (data?.conversationId) setConversationId(data.conversationId);
              if (data?.undoableLogId) {
                setUndoable({ logId: data.undoableLogId, expiresAt: Date.now() + 10 * 60 * 1000 });
              } else {
                setUndoable(null);
              }
              // El mensaje consumió del pool de Gustavo + créditos del agente → refrescar en vivo
              // el cupo (Subscription) y el saldo (Credits), sin esperar a un re-login.
              dispatch(baseApi.util.invalidateTags(['Subscription', 'Credits']));
              toolsDelTurno.forEach((name) => onToolUsed?.(name));
            },
            onError: (data) => {
              errored = true;
              // Bloqueo por cupo/créditos → refrescar el estado en el acto para que el input
              // quede deshabilitado y el banner aparezca sin esperar a que el usuario re-loguee.
              dispatch(baseApi.util.invalidateTags(['Subscription', 'Credits']));
              // Se acabaron los créditos del AGENTE (wallet) → comprar más.
              if (data?.kind === 'insufficient_credits') {
                onInsufficientCredits?.(data as InsufficientCreditsError);
                setMessages((prev) => [
                  ...prev,
                  {
                    id: `sys-${Date.now()}`,
                    role: 'system' as const,
                    kind: 'no_credits' as const,
                    reason: 'wallet' as const,
                    balance:  data.balance ?? 0,
                    quota:         data.quota ?? null,
                    renewsMonthly: !!data.renewsMonthly,
                    createdAt: new Date(),
                  },
                ]);
              } else if (data?.kind === 'gustavo_quota_exceeded') {
                // Se agotó el cupo mensual del PLAN de la org → subir de plan / esperar renovación.
                setMessages((prev) => [
                  ...prev,
                  {
                    id: `sys-${Date.now()}`,
                    role: 'system' as const,
                    kind: 'no_credits' as const,
                    reason: 'quota' as const,
                    limit: typeof data.limit === 'number' ? data.limit : undefined,
                    createdAt: new Date(),
                  },
                ]);
              } else {
                setError(data?.message ?? 'Error desconocido');
              }
            },
            onCredits: (data) => {
              if (data && typeof data.balance === 'number' && onCredits) {
                onCredits(data as CreditsEvent);
              }
            },
          });
        }
      }
    } catch (e: any) {
      errored = true;
      if (e.name !== 'AbortError') {
        setError(e.message ?? 'Error al procesar el mensaje');
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
      // Si hubo error y no llegó a streamear nada, sacamos el bubble vacío del asistente
      // (si no, se queda mostrando los 3 puntitos de typing para siempre).
      if (errored && !accumulatedContent) {
        setMessages((prev) => prev.filter((m) => m.id !== assistantId));
      }
    }
  }, [accessToken, conversationId, isStreaming, onNavigate, onCredits, onInsufficientCredits, onToolUsed]);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
  }, []);

  const clearUndoable = useCallback(() => setUndoable(null), []);

  return { messages, isStreaming, conversationId, error, send, abort, resetTo, prepend, undoable, clearUndoable };
}

function parseEvent(raw: string, handlers: {
  onDelta:     (text: string) => void;
  onToolStart: (name: string) => void;
  onToolEnd:   (name: string) => void;
  onCreated:   (data: any) => void;
  onNavigate:  (data: any) => void;
  onDone:      (data: any) => void;
  onError:     (data: any) => void;
  onCredits:   (data: any) => void;
}) {
  const lines = raw.split('\n');
  let eventType = '';
  let dataStr = '';
  for (const line of lines) {
    if (line.startsWith('event:')) eventType = line.slice(6).trim();
    else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
  }
  if (!eventType) return;

  let data: any;
  try { data = dataStr ? JSON.parse(dataStr) : null; } catch { data = dataStr; }

  switch (eventType) {
    case 'delta':
      if (typeof data === 'string') handlers.onDelta(data);
      break;
    case 'tool_start':
      if (data?.name) handlers.onToolStart(data.name);
      break;
    case 'tool_end':
      if (data?.name) handlers.onToolEnd(data.name);
      break;
    case 'conversation_created':
      handlers.onCreated(data);
      break;
    case 'navigate':
      handlers.onNavigate(data);
      break;
    case 'done':
      handlers.onDone(data);
      break;
    case 'error':
      handlers.onError(data);
      break;
    case 'credits':
      handlers.onCredits(data);
      break;
  }
}
